// Pricing migration 17b (ADO-3948): which products a list leaves out for want of a price.
//
// `epics.md` E11-D9 (2026-10-29, widened the same day on ADO-3948): a product the pricing rules
// price at zero, or that has no price for a reason of its own, is not displayed in product
// discovery. The pricing interface answers such a product `unpriced` with its reason, and the web
// hides by that reason alone: it decides nothing about prices itself.
//
// Lists only. A product looked up directly (its product page, a barcode scan, an exact-code
// search hit) is still shown, in row 17's "couldn't be priced" state with Add blocked (AC5).
//
// A list waits for its whole set of price answers and then draws once, already filtered
// (DEC-2), so a card never appears and then vanishes (AC2). Everything here is behind
// `VITE_REAL_PRICES_ENABLED`: with it off no product is hidden (AC6).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { UnpricedReason } from '../api/price-quotes.client';
import type { SearchResultProduct } from '../api/search.client';
import { LOADING_TEXT_DELAY_MS } from './ProductPrice';
import { realPricesEnabled } from './realPrices';
import { useCurrentPriceKey, usePriceStore, usePrices, type PriceEntry } from './usePriceStore';

/**
 * The unpriced reasons that leave a product out of every list (E11-D9, ADO-3948 AC1, AC1b):
 * the product has no price of its own.
 *
 * - `ZERO_PRICE`: the pricing rules price it at zero.
 * - `NO_PRICE`: no rule prices it (for example no WOW deal in a WOW cart).
 * - `PRICE_NOT_REPRESENTABLE`: the price is negative, below a cent or too large.
 * - `DEPOSIT_UNAVAILABLE`: its deposit data is missing or bad.
 * - `PRODUCT_NOT_IN_PRICING_SCOPE`: the store's pricing does not hold this product (no price
 *   group in the store's matrix, or no catalogue row; ADO-3988).
 *
 * Absent on purpose, because each is a failure of the store's setup and hiding on it would empty
 * every list for that store (AC3): `NOT_IN_PRICING_SCOPE` (the store-wide one: depot,
 * authorisation, range listings, profile price groups), `ACCOUNT_UNRESOLVED` and
 * `RANGE_UNRESOLVED` (the price endpoint's own), and the web's `ENTRY_FORBIDDEN` (a 403). A
 * product unpriced for one of those stays on screen, "couldn't be priced", as does a product
 * whose price request failed (with Retry). A reason this web does not know is shown too.
 *
 * Every reason is listed, so a reason added to `UnpricedReason` fails to compile here until it is
 * decided, rather than silently staying on screen.
 */
const HIDES_FROM_LISTS = {
  ZERO_PRICE: true,
  NO_PRICE: true,
  PRICE_NOT_REPRESENTABLE: true,
  DEPOSIT_UNAVAILABLE: true,
  PRODUCT_NOT_IN_PRICING_SCOPE: true,
  NOT_IN_PRICING_SCOPE: false,
  ACCOUNT_UNRESOLVED: false,
  RANGE_UNRESOLVED: false,
  ENTRY_FORBIDDEN: false,
} as const satisfies Record<UnpricedReason, boolean>;

/** The reasons {@link HIDES_FROM_LISTS} hides. */
export const HIDDEN_UNPRICED_REASONS: ReadonlySet<UnpricedReason> = new Set(
  (Object.keys(HIDES_FROM_LISTS) as UnpricedReason[]).filter((reason) => HIDES_FROM_LISTS[reason]),
);

/** True only for a product answered `unpriced` with a reason in {@link HIDDEN_UNPRICED_REASONS}.
 *  No answer yet, a price, a failed request or a store-setup reason never hides it. */
export function isHiddenFromLists(entry: PriceEntry | undefined): boolean {
  return entry?.state === 'unpriced' && HIDDEN_UNPRICED_REASONS.has(entry.reason);
}

/**
 * False for a product whose card draws no price and is never asked for one: a `notAvailable`
 * search or favourites row, and an off-sale RBP parent shown for its links
 * (`rbpLinks: 'OFFERED'`). `ProductCard` draws both as its not-available card. Such a product is
 * never hidden for want of a price; pass this as `isPriceable` for a list of browse cards.
 */
export function drawsItsPrice(
  product: Pick<SearchResultProduct, 'notAvailable' | 'rbpLinks'>,
): boolean {
  return product.notAvailable !== true && product.rbpLinks !== 'OFFERED';
}

/**
 * The longest a list holds for its prices (ADO-3948, decided with the story owner: fail open).
 * After it, the list draws the products still waiting in their loading state, so a slow or hung
 * price request never empties the catalogue. The same moment a card's slot says "Loading price…"
 * (`LOADING_TEXT_DELAY_MS`). The cost, accepted: on a slow day a product with no price of its own
 * may stay on screen. Follow-up: a request timeout in the shared HTTP client (`apiFetch`),
 * recorded in `deferred-work.md`.
 */
export const LIST_PRICE_HOLD_MS = LOADING_TEXT_DELAY_MS;

export interface ListedProductsOptions<T> {
  /** The SKU an item is priced by. An item whose SKU is blank is never asked for a price and
   *  passes through. */
  readonly skuOf: (item: T) => string;
  /** False for an item that is never asked for a price, which passes through untouched: a
   *  `notAvailable` search row, an `rbpLinks: 'OFFERED'` card. Every item is priceable when
   *  left out. */
  readonly isPriceable?: (item: T) => boolean;
  /** True for an item that is never hidden whatever its price answer: an exact search hit
   *  (DEC-1), which the user looked up. It still holds the list until its answer arrives. */
  readonly keep?: (item: T) => boolean;
}

export interface ListedProducts<T> {
  /** The items the list draws, in their order: empty while settling. */
  readonly shown: readonly T[];
  /** True while a priceable item has no answer yet: the list shows its own loading state. */
  readonly settling: boolean;
}

/** What one hook instance has already settled for the current price key. */
interface Settled {
  readonly key: string | null;
  /** SKUs that have had an answer: a Retry sends one back to `loading`, which must not hold
   *  the whole list again (DEC-2: a failed answer never holds the list). */
  readonly answered: Set<string>;
  /** SKUs this list has drawn: a later answer never takes one away in front of the user (AC2). */
  readonly drawn: Set<string>;
}

/**
 * The items of a product list that the list draws (ADO-3948 AC1, AC2, AC3, AC6; DEC-2).
 *
 * Asks for the prices of every priceable item, hidden ones included, in one batched request
 * (`usePrices`), and holds the list (`settling`, nothing shown) until each has an answer, or for
 * {@link LIST_PRICE_HOLD_MS} at most: then the items still waiting are drawn in their loading state
 * and count as drawn. Then it draws once, leaving out each item {@link isHiddenFromLists} hides,
 * unless `keep` matches it. The deadline restarts when the list's priceable SKUs or the price key
 * change.
 * A failed answer counts as an answer, and an item that `isPriceable` rejects is never asked for
 * a price and passes through.
 *
 * Once settled, a list stays settled: an item answered before and asked again (a Retry) never
 * holds it again, and an item already drawn is never taken away. Both hold only while the item
 * stays on the list: an item that leaves it (another page, another search) is decided afresh if
 * it comes back, and both are reset when the ordering entry or trading account changes.
 *
 * Reads entries as `usePriceEntry` does: an entry held for another price key counts as absent.
 * With the flag off, or with no ordering entry chosen, every item is shown and nothing settles.
 * `shown` keeps its identity while its items do, so it is safe in an effect's dependencies.
 */
export function useListedProducts<T>(
  items: readonly T[],
  options: ListedProductsOptions<T>,
): ListedProducts<T> {
  const enabled = realPricesEnabled();
  const currentKey = useCurrentPriceKey();
  const active = enabled && currentKey !== null;
  const { skuOf, isPriceable, keep } = options;

  const priceable = (item: T): boolean =>
    skuOf(item) !== '' && (isPriceable === undefined || isPriceable(item));
  const priceableSkus = active ? items.filter(priceable).map(skuOf) : [];
  usePrices(priceableSkus);

  // Only this list's entries, compared shallowly: a price answer for another SKU on the page does
  // not re-render this list (every orderable card runs this hook for its substitutes).
  const entries = usePriceStore(
    useShallow((s) => {
      const picked: Record<string, PriceEntry | undefined> = {};
      if (!active || s.key !== currentKey) return picked;
      for (const sku of priceableSkus) picked[sku] = s.entries[sku];
      return picked;
    }),
  );

  const settledRef = useRef<Settled>({ key: null, answered: new Set(), drawn: new Set() });
  if (settledRef.current.key !== currentKey) {
    settledRef.current = { key: currentKey, answered: new Set(), drawn: new Set() };
  }
  const settled = settledRef.current;

  // The hold's deadline, for this price key and this set of priceable SKUs.
  const holdKey = `${currentKey ?? ''}|${[...new Set(priceableSkus)].sort().join(',')}`;
  const [holdExpiredFor, setHoldExpiredFor] = useState<string | null>(null);
  const holdExpired = holdExpiredFor === holdKey;
  // A latch holds only while its item stays on the list it was set on.
  const onTheList = new Set(priceableSkus);
  for (const latch of [settled.answered, settled.drawn]) {
    for (const sku of latch) if (!onTheList.has(sku)) latch.delete(sku);
  }

  let settling = false;
  const next: T[] = [];
  /** The SKUs drawn on their own answer, not only because `keep` matched them: a kept exact hit
   *  must not stay drawn when the same product later turns up as a keyword hit. */
  const drawnOnTheirAnswer: string[] = [];
  if (active) {
    for (const item of items) {
      if (!priceable(item)) {
        next.push(item);
        continue;
      }
      const sku = skuOf(item);
      const entry = entries[sku];
      if (entry !== undefined && entry.state !== 'loading') settled.answered.add(sku);
      else if (!settled.answered.has(sku) && !holdExpired) settling = true;
      if (settled.drawn.has(sku) || !isHiddenFromLists(entry)) {
        next.push(item);
        drawnOnTheirAnswer.push(sku);
      } else if (keep?.(item) === true) {
        next.push(item);
      }
    }
  }

  const candidate: readonly T[] = !active ? items : settling ? [] : next;
  if (active && !settling) {
    for (const sku of drawnOnTheirAnswer) settled.drawn.add(sku);
  } else {
    // A held list draws nothing, so nothing on it can be taken away in front of the user.
    settled.drawn.clear();
  }

  // Keep the previous array while it holds the same items, so a surface's effect or memo keyed
  // on `shown` does not re-run on every render.
  const previousRef = useRef<readonly T[]>(candidate);
  const previous = previousRef.current;
  const same =
    previous.length === candidate.length &&
    previous.every((item, index) => item === candidate[index]);
  const shown = same ? previous : candidate;
  previousRef.current = shown;

  useEffect(() => {
    if (!settling) return;
    const timer = setTimeout(() => setHoldExpiredFor(holdKey), LIST_PRICE_HOLD_MS);
    return () => clearTimeout(timer);
  }, [holdKey, settling]);

  return useMemo(() => ({ shown, settling }), [shown, settling]);
}
